import React from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

export default function FundsTable({ allFunds, formatLargeNumber }) {
  const columns = [
    {
      header: () => (
        <>
          Ticker<br />Fund name
        </>
      ),
      accessorKey: 'longName',
      cell: ({ row }) => {
        const fund = row.original;
        return (
          <>
            <p className="mb-1 fw-bold">
              <Link
                to="/idetails"
                state={{ symbol: fund.symbol }}
                className="text-white"
                style={{ textDecoration: 'none' }}
              >
                {fund.longName}
              </Link>
            </p>
            <div className="d-flex align-items-center">
              <span
                className="px-2 fw-bold text-uppercase"
                style={{ border: '1px solid rgb(117, 117, 117)' }}
              >
                {fund.symbol}
              </span>
              <span className="ms-2">Factsheet</span>
            </div>
          </>
        );
      },
    },
    { header: 'Share Class', accessorKey: 'quoteType', cell: info => info.getValue() || '--' },
    { header: 'Currency', accessorKey: 'currency' },
    {
      header: 'Incept date',
      accessorKey: 'firstTradeDateMilliseconds',
      cell: ({ getValue }) =>
        new Date(getValue()).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }),
    },
    {
      header: 'Net assets (USD)',
      accessorKey: 'netAssets',
      cell: ({ getValue }) => formatLargeNumber(getValue()),
    },
    {
      header: 'Gross expense ratio',
      accessorKey: 'netExpenseRatio',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: 'Net expense ratio',
      accessorKey: 'netExpenseRatio',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: 'YTD (%)',
      accessorKey: 'ytdReturn',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: '1Y (%)',
      accessorKey: 'annualReturnNavY1',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: '3Y (%)',
      accessorKey: 'annualReturnNavY3',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: '5Y (%)',
      accessorKey: 'annualReturnNavY5',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: '10Y (%)',
      accessorKey: 'annualReturnNavY10',
      cell: ({ getValue }) => (getValue() ? getValue().toFixed(2) : '--'),
    },
    {
      header: 'Perf. as of',
      cell: () =>
        new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }),
    },
  ];

  const table = useReactTable({
    data: allFunds || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // ✅ Show 10 rows per page
      },
    },
  });

  return (
    <>
      <table className="table custom-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} style={{ verticalAlign: 'top' }}>
              {hg.headers.map((header) => (
                <th key={header.id} className="text-nowrap">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} style={{ verticalAlign: 'middle' }}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ✅ Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div
          className="myBtn rounded-0 fw-semibold bg-transparent" style={{fontSize:'14px',border:'2px solid #ff6b35'}}
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          ← Previous
        </div>
        <span className='text-prime fw-semibold' style={{fontSize:'14px'}}>
          Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
          {table.getPageCount()}
        </span>

        <div
          className="myBtn rounded-0 fw-semibold" style={{fontSize:'14px'}}
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next →
        </div>
      </div>
    </>
  );
}
